import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUpload, FiCheck, FiAlertCircle, FiFile, FiTrash2 } from 'react-icons/fi';
import { submitInquiry } from '../../services/api';
import RateLimitModal from './RateLimitModal';

function InquiryModal({ isOpen, onClose, defaultInquiryType = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    inquiry_type: defaultInquiryType,
    requirement_summary: '',
    consent: false,
    honeypot: '', // Anti-spam honeypot field
  });

  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | 'rate_limited'
  const [errorMessage, setErrorMessage] = useState('');
  const [fileErrorMessage, setFileErrorMessage] = useState(''); // Separate state for file-specific errors
  const [referenceId, setReferenceId] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [rateLimitData, setRateLimitData] = useState(null);
  const [shouldShake, setShouldShake] = useState(false); // State to trigger shake animation

  // Update inquiry_type when defaultInquiryType changes
  useEffect(() => {
    if (defaultInquiryType) {
      setFormData(prev => ({ ...prev, inquiry_type: defaultInquiryType }));
    }
  }, [defaultInquiryType]);

  // Ref to track if cleanup should actually restore scroll
  const shouldRestoreScrollRef = useRef(true);
  
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      shouldRestoreScrollRef.current = true;
      
      // Check if body is already locked (transitioning from another modal)
      const isAlreadyLocked = document.body.style.position === 'fixed';
      const existingTop = document.body.style.top;
      
      // Get scroll position from various sources in priority order:
      // 1. Window global (persists across modal transitions)
      // 2. Existing body.style.top (if already locked)
      // 3. Current window.scrollY
      let scrollY;
      if (window.__modalScrollY !== undefined) {
        scrollY = window.__modalScrollY;
      } else if (isAlreadyLocked && existingTop) {
        scrollY = Math.abs(parseInt(existingTop, 10)) || 0;
        window.__modalScrollY = scrollY;
      } else {
        scrollY = window.scrollY;
        window.__modalScrollY = scrollY;
      }
      
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Only restore if this is the actual close, not a StrictMode re-run
        // StrictMode runs effect, cleanup, effect - so we check if the effect runs again
        shouldRestoreScrollRef.current = false;
        
        // Defer the actual cleanup to next tick to see if effect runs again
        setTimeout(() => {
          // If shouldRestoreScrollRef is still false, it means effect didn't run again (actual close)
          if (!shouldRestoreScrollRef.current && window.__modalScrollY !== undefined) {
            const savedScrollY = window.__modalScrollY;
            delete window.__modalScrollY;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.overflow = '';
            window.scrollTo(0, savedScrollY);
          }
        }, 0);
      };
    }
  }, [isOpen]);

  const inquiryTypes = [
    'Request Quote',
    'Technical Discussion',
    'Factory Visit',
    'Upload RFQ',
    'Service Request',
    'Feedback',
  ];

  const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'zip'];
  const maxFileSize = 25 * 1024 * 1024; // 25MB per file
  const maxFiles = 3;

  // Check if file upload is required (only for "Upload RFQ")
  const isFileRequired = formData.inquiry_type === 'Upload RFQ';

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Trigger shake animation
  const triggerShake = () => {
    setShouldShake(true);
    setTimeout(() => setShouldShake(false), 500);
  };

  // Shake animation variants for framer-motion
  const shakeAnimation = {
    shake: {
      x: [0, -10, 10, -10, 10, -5, 5, 0],
      transition: { duration: 0.5 }
    }
  };

  // Validate individual field
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return value.trim() ? '' : 'Name is required';
      case 'organization':
        return value.trim() ? '' : 'Organization is required';
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) return 'Email is required';
        if (!emailRegex.test(value)) return 'Please enter a valid email';
        return '';
      case 'phone':
        if (!value.trim()) return 'Phone is required';
        if (value.replace(/\D/g, '').length < 10) return 'Please enter a valid phone number';
        return '';
      case 'inquiry_type':
        return value ? '' : 'Please select an inquiry type';
      case 'requirement_summary':
        return value.trim() ? '' : 'Requirement summary is required';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear field error on change
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFileErrorMessage('');
    
    const errors = [];
    const validFiles = [];
    const currentFileCount = files.length;
    const remainingSlots = maxFiles - currentFileCount;

    // Check if trying to add more files than allowed
    if (selectedFiles.length > remainingSlots) {
      if (remainingSlots === 0) {
        errors.push(`Maximum ${maxFiles} files allowed`);
      } else {
        errors.push(`Only ${remainingSlots} more file${remainingSlots > 1 ? 's' : ''} can be added (max ${maxFiles} files)`);
      }
    }

    // Process only up to remaining slots
    const filesToProcess = selectedFiles.slice(0, Math.max(0, remainingSlots));

    for (const selectedFile of filesToProcess) {
      // Check file size
      if (selectedFile.size > maxFileSize) {
        errors.push(`File "${selectedFile.name}" exceeds 25MB limit`);
        continue;
      }

      // Check file type
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(fileExt)) {
        errors.push(`File type .${fileExt} not allowed`);
        continue;
      }

      // Check for duplicate
      if (files.some(f => f.name === selectedFile.name && f.size === selectedFile.size)) {
        errors.push(`File "${selectedFile.name}" is already added`);
        continue;
      }

      // File is valid
      validFiles.push(selectedFile);
    }

    // Add valid files
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }

    // Show errors if any
    if (errors.length > 0) {
      setFileErrorMessage(errors.join(' • '));
      triggerShake();
    }

    // Reset input
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFileErrorMessage('');
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    const requiredFields = ['name', 'organization', 'email', 'phone', 'inquiry_type', 'requirement_summary'];
    const hasAllFields = requiredFields.every(field => formData[field]?.trim?.() || formData[field]);
    const hasConsent = formData.consent;
    const hasFileIfRequired = !isFileRequired || files.length > 0;
    const hasNoFieldErrors = Object.values(fieldErrors).every(error => !error);
    
    return hasAllFields && hasConsent && hasFileIfRequired && hasNoFieldErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors = {};
    ['name', 'organization', 'email', 'phone', 'inquiry_type', 'requirement_summary'].forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      setErrorMessage('Please fix the errors below');
      return;
    }

    if (!formData.consent) {
      setErrorMessage('Please accept the consent to proceed');
      return;
    }

    if (isFileRequired && files.length === 0) {
      setErrorMessage('File upload is required for RFQ submissions');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('organization', formData.organization);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('inquiry_type', formData.inquiry_type);
      submitData.append('requirement_summary', formData.requirement_summary);
      submitData.append('consent', formData.consent);
      submitData.append('honeypot', formData.honeypot);

      // Append all files
      files.forEach((file, index) => {
        submitData.append(`file${index > 0 ? index : ''}`, file);
      });

      const response = await submitInquiry(submitData);
      
      // Get reference ID from response
      setReferenceId(response.data.reference_id || '');
      setSubmitStatus('success');
      
    } catch (error) {
      console.error('Submission error:', error);
      
      // Check if it's a rate limit error (429)
      if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'object' && detail.message === 'Submission limit reached') {
          // Set rate limit data and show rate limit modal
          setRateLimitData(detail);
          setSubmitStatus('rate_limited');
        } else {
          setSubmitStatus('error');
          setErrorMessage(typeof detail === 'string' ? detail : 'Too many submissions. Please try again later.');
        }
      } else {
        setSubmitStatus('error');
        const detail = error.response?.data?.detail;
        setErrorMessage(typeof detail === 'string' ? detail : 'Failed to submit inquiry. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form after animation completes
    setTimeout(() => {
      setFormData({
        name: '',
        organization: '',
        email: '',
        phone: '',
        inquiry_type: '',
        requirement_summary: '',
        consent: false,
        honeypot: '',
      });
      setFiles([]);
      setSubmitStatus(null);
      setReferenceId('');
      setFieldErrors({});
      setErrorMessage('');
      setFileErrorMessage('');
      setRateLimitData(null);
      setShouldShake(false);
    }, 300);
  };

  const handleRateLimitClose = () => {
    setRateLimitData(null);
    setSubmitStatus(null);
    handleClose();
  };

  // Show rate limit modal if rate limited
  if (submitStatus === 'rate_limited' && rateLimitData) {
    return (
      <RateLimitModal
        isOpen={true}
        onClose={handleRateLimitClose}
        rateLimitData={rateLimitData}
      />
    );
  }

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <div 
        className="fixed inset-0 flex items-start justify-center pt-4 md:pt-28 pb-4 md:pb-8 px-4 bg-black/50 backdrop-blur-sm" 
        style={{ zIndex: 10002 }}
        data-testid="inquiry-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={shouldShake ? { opacity: 1, scale: 1, y: 0, x: [0, -8, 8, -8, 8, -4, 4, 0] } : { opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={shouldShake ? { duration: 0.4, ease: "easeInOut" } : {}}
          className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[95vh] md:max-h-[calc(100vh-160px)] overflow-y-auto"
          style={{
            scrollbarWidth: 'none', /* Firefox */
            msOverflowStyle: 'none', /* IE/Edge */
            overscrollBehavior: 'contain', /* Prevent scroll bleed */
          }}
        >
          <style>{`
            [data-testid="inquiry-modal"] > div::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
            <h2 className="text-2xl font-bold text-dark-900">Contact Us</h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
              data-testid="inquiry-modal-close-btn"
            >
              <FiX />
            </button>
          </div>

          {/* Success State */}
          {submitStatus === 'success' && (
            <div className="p-6">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <FiCheck className="text-4xl text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-dark-900 mb-2">Thank You!</h3>
                <p className="text-gray-600 max-w-md mb-6">
                  Your inquiry has been submitted successfully. Our team will review it and get back to you within 24-48 hours.
                </p>
                
                {/* Reference ID Display */}
                {referenceId && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 mb-6">
                    <p className="text-sm text-gray-600 mb-1">Your Reference ID</p>
                    <p className="text-2xl font-mono font-bold tracking-wider" style={{ color: '#101957' }}>
                      {referenceId}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Please save this for your records
                    </p>
                  </div>
                )}

                <button
                  onClick={handleClose}
                  className="px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Form */}
          {submitStatus !== 'success' && (
            <form onSubmit={handleSubmit} className="p-6">
              {/* General Error Message (for submission/validation errors only) */}
              {errorMessage && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start" data-testid="error-message">
                  <FiAlertCircle className="text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-red-800 text-sm">{errorMessage}</p>
                </div>
              )}

              {/* Honeypot (hidden) */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleInputChange}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="e.g., Rajesh Kumar"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      fieldErrors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                    data-testid="inquiry-form-name"
                  />
                  {fieldErrors.name && (
                    <p className="text-red-600 text-xs mt-1">{fieldErrors.name}</p>
                  )}
                </div>

                {/* Organization */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Organization <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="e.g., Defense Research Lab"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      fieldErrors.organization ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                    data-testid="inquiry-form-organization"
                  />
                  {fieldErrors.organization && (
                    <p className="text-red-600 text-xs mt-1">{fieldErrors.organization}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="e.g., rajesh.kumar@organization.in"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                    data-testid="inquiry-form-email"
                  />
                  {fieldErrors.email && (
                    <p className="text-red-600 text-xs mt-1">{fieldErrors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="e.g., +91 98765 43210"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      fieldErrors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                    data-testid="inquiry-form-phone"
                  />
                  {fieldErrors.phone && (
                    <p className="text-red-600 text-xs mt-1">{fieldErrors.phone}</p>
                  )}
                </div>

                {/* Inquiry Type */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Inquiry Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="inquiry_type"
                    value={formData.inquiry_type}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      fieldErrors.inquiry_type ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    required
                    data-testid="inquiry-form-type"
                  >
                    <option value="">Select inquiry type</option>
                    {inquiryTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.inquiry_type && (
                    <p className="text-red-600 text-xs mt-1">{fieldErrors.inquiry_type}</p>
                  )}
                </div>

                {/* Requirement Summary */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Requirement Summary <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="requirement_summary"
                    value={formData.requirement_summary}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    rows="4"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      fieldErrors.requirement_summary ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., We require a custom thermal management system for an airborne platform. Operating temperature range: -40°C to +70°C..."
                    required
                    data-testid="inquiry-form-summary"
                  />
                  {fieldErrors.requirement_summary && (
                    <p className="text-red-600 text-xs mt-1">{fieldErrors.requirement_summary}</p>
                  )}
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-semibold text-dark-900 mb-2">
                    Upload RFQ / Requirements Document {isFileRequired ? <span className="text-red-500">*</span> : '(Optional)'}
                  </label>
                  
                  {/* Defense-friendly warning */}
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800">
                      <strong>⚠️ Security Notice:</strong> Do not upload classified or restricted documents. Upload only sharable RFQs/specifications suitable for civilian transmission.
                    </p>
                  </div>

                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isFileRequired && files.length === 0 ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-primary-500'
                  }`}>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.zip"
                      className="hidden"
                      id="file-upload"
                      multiple
                      data-testid="inquiry-form-file"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <FiUpload className="text-4xl text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        PDF, DOC, DOCX, XLS, XLSX, ZIP (Max 25MB per file, up to {maxFiles} files)
                      </span>
                    </label>
                  </div>

                  {/* File Error Message - Below Upload Box */}
                  <AnimatePresence>
                    {fileErrorMessage && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start"
                        data-testid="file-error-message"
                      >
                        <FiAlertCircle className="text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                        <p className="text-red-800 text-sm">{fileErrorMessage}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Selected Files List */}
                  {files.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {files.map((file, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center min-w-0 flex-1">
                            <FiFile className="text-gray-500 mr-2 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="ml-3 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title="Remove file"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {isFileRequired && files.length === 0 && (
                    <p className="text-red-600 text-xs mt-2">
                      File upload is required for RFQ submissions
                    </p>
                  )}
                </div>

                {/* Consent with Privacy Policy */}
                <div className="space-y-3">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleInputChange}
                      className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      required
                      data-testid="inquiry-form-consent"
                    />
                    <label className="text-sm text-gray-600">
                      I consent to HADEN Systems storing and processing my information to respond to this inquiry. <span className="text-red-500">*</span>
                    </label>
                  </div>
                  
                  {/* Privacy & Retention Notice */}
                  <div className="ml-7 text-xs text-gray-500 space-y-1">
                    <p>
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                        Privacy Policy
                      </a>
                      {' '}&bull;{' '}
                      Uploaded files are retained for 90 days and then securely deleted.
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !isFormValid()}
                  className={`w-full py-4 font-semibold rounded-lg transition-colors ${
                    isFormValid() && !isSubmitting
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  data-testid="inquiry-form-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <div className="spinner mr-3"></div>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Inquiry'
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}

export default InquiryModal;
